import type {Page, Result} from 'framework';
import type {ApplyProposal} from '../application/use-cases/apply-proposal';
import type {CreateConversation} from '../application/use-cases/create-conversation';
import type {DeleteAttachment} from '../application/use-cases/delete-attachment';
import type {DeleteConversation} from '../application/use-cases/delete-conversation';
import type {DiscardProposal} from '../application/use-cases/discard-proposal';
import type {GetConversation} from '../application/use-cases/get-conversation';
import type {ListConversations} from '../application/use-cases/list-conversations';
import type {UploadAttachment} from '../application/use-cases/upload-attachment';
import type {
  DeleteAttachmentInput,
  ListConversationsInput,
  ProposalDecisionInput,
  UploadAttachmentInput,
} from '../application/ports/assistant.repository';
import type {AssistantAttachment} from '../domain/assistant-attachment';
import type {AssistantProposal} from '../domain/assistant-proposal';
import type {AssistantError} from '../domain/errors';
import type {Conversation, ConversationDetail} from '../domain/conversation';
import type {ConversationId} from '../domain/conversation-id.value-object';

export class AssistantController {
  constructor(
    private readonly listConversations: ListConversations,
    private readonly getConversation: GetConversation,
    private readonly createConversation: CreateConversation,
    private readonly deleteConversation: DeleteConversation,
    private readonly applyProposalUseCase: ApplyProposal,
    private readonly discardProposalUseCase: DiscardProposal,
    private readonly uploadAttachmentUseCase: UploadAttachment,
    private readonly deleteAttachmentUseCase: DeleteAttachment,
  ) {}

  list(input: ListConversationsInput): Promise<Result<Page<Conversation>, AssistantError>> {
    return this.listConversations.execute(input);
  }

  getById(id: ConversationId): Promise<Result<ConversationDetail, AssistantError>> {
    return this.getConversation.execute(id);
  }

  create(): Promise<Result<Conversation, AssistantError>> {
    return this.createConversation.execute();
  }

  delete(id: ConversationId): Promise<Result<void, AssistantError>> {
    return this.deleteConversation.execute(id);
  }

  applyProposal(input: ProposalDecisionInput): Promise<Result<AssistantProposal, AssistantError>> {
    return this.applyProposalUseCase.execute(input);
  }

  discardProposal(
    input: ProposalDecisionInput,
  ): Promise<Result<AssistantProposal, AssistantError>> {
    return this.discardProposalUseCase.execute(input);
  }

  uploadAttachment(
    input: UploadAttachmentInput,
  ): Promise<Result<AssistantAttachment, AssistantError>> {
    return this.uploadAttachmentUseCase.execute(input);
  }

  deleteAttachment(input: DeleteAttachmentInput): Promise<Result<void, AssistantError>> {
    return this.deleteAttachmentUseCase.execute(input);
  }
}
